/**
 * Shared sport data constants, labels, and segment builder utilities.
 * Centralizes definitions previously duplicated across DuelScorePage, ArchivePage,
 * SportBreakdownBar, and OpponentProfileCard.
 */

/* ── Colors ──────────────────────────────────────────────────────────────── */

export const SPORT_COLORS: Record<string, string> = {
  Run:            '#e85d04',
  TrailRun:       '#dc2f02',
  VirtualRun:     '#f48c06',
  Ride:           '#2573d3',
  VirtualRide:    '#1a5fad',
  MountainBikeRide: '#1a5fad',
  Swim:           '#0096c7',
  Rowing:         '#48cae4',
  Walk:           '#80b918',
  Hike:           '#55a630',
  WeightTraining: '#9b5de5',
  Workout:        '#7b2d8b',
  Crossfit:       '#6d23b6',
  Yoga:           '#b5179e',
  _bonus:         '#ffb63c',
  _pr:            '#ffd60a',
  _other:         '#666',
};

/* ── Short labels (for compact display) ──────────────────────────────────── */

export function getSportLabel(sport: string): string {
  const map: Record<string, string> = {
    Run: 'Run', TrailRun: 'Trail', VirtualRun: 'Run',
    Ride: 'Ride', VirtualRide: 'Ride', MountainBikeRide: 'MTB',
    Swim: 'Swim', Walk: 'Walk', Hike: 'Hike', Rowing: 'Row',
    WeightTraining: 'Weights', Workout: 'Workout', Crossfit: 'Crossfit',
    Yoga: 'Yoga', Consolation: 'Cons', RestDay: 'Rest',
  };
  return map[sport] ?? sport;
}

/* ── Full labels (for profile text) ──────────────────────────────────────── */

export const SPORT_LABEL: Record<string, string> = {
  Run: 'Run', TrailRun: 'Trail', VirtualRun: 'Virtual Run',
  Ride: 'Ride', VirtualRide: 'Virtual Ride', MountainBikeRide: 'MTB',
  Swim: 'Swim', Walk: 'Walk', Hike: 'Hike',
  WeightTraining: 'Weights', Workout: 'Workout', Crossfit: 'Crossfit',
  Yoga: 'Yoga', Rowing: 'Row',
};

/* ── Verb forms (for natural-language profiles) ───────────────────────────── */

export const SPORT_VERB: Record<string, string> = {
  Run: 'runs', TrailRun: 'trail runs', VirtualRun: 'virtual runs',
  Ride: 'rides', VirtualRide: 'virtual rides', MountainBikeRide: 'MTB rides',
  Swim: 'swims', Walk: 'walks', Hike: 'hikes',
  WeightTraining: 'weight sessions', Workout: 'workouts', Crossfit: 'CrossFit sessions',
  Yoga: 'yoga sessions', Rowing: 'rows',
};

/* ── Segment builder ─────────────────────────────────────────────────────── */

export interface BarSegment {
  key: string;
  color: string;
  widthPct: number;
  title: string;
  label: string;
}

/**
 * Builds stacked-bar segments from an array of scored activities.
 * Used by DuelScorePage (SportBreakdown), ArchivePage, and SportBreakdownBar.
 */
export function buildSportSegments(
  activities: Array<{
    sport_type: string;
    base_points?: number | string | null;
    challenge_bonus_points?: number | string | null;
    pr_bonus_points?: number | string | null;
  }>,
  maxTotal: number,
): BarSegment[] {
  const byType: Record<string, number> = {};
  let bonusPts = 0;

  for (const a of activities) {
    const base = Number(a.base_points) || 0;
    const isSpecial = ['BadgeReward','Consolation','ChallengeKing','Multisport','SportJunky','RestDay','Betting','DailySpin'].includes(a.sport_type);
    if (base > 0 && !isSpecial) {
      byType[a.sport_type] = (byType[a.sport_type] ?? 0) + base;
    }
    if (isSpecial) {
      bonusPts += base;
    }
    bonusPts += Number(a.challenge_bonus_points) || 0;
    bonusPts += Number(a.pr_bonus_points) || 0;
    bonusPts += Number((a as any).trophy_boost_amount) || 0;
  }

  const sorted = Object.entries(byType).sort((a, b) => b[1] - a[1]);
  const segs: BarSegment[] = [];

  for (const [sport, pts] of sorted) {
    const w = maxTotal > 0 ? (pts / maxTotal) * 100 : 0;
    segs.push({
      key: sport,
      color: SPORT_COLORS[sport] ?? SPORT_COLORS['_other'],
      widthPct: w,
      title: `${getSportLabel(sport)}: ${Math.round(pts)} pts`,
      label: getSportLabel(sport),
    });
  }
  if (bonusPts > 0) {
    const w = maxTotal > 0 ? (bonusPts / maxTotal) * 100 : 0;
    segs.push({
      key: '_bonus',
      color: SPORT_COLORS['_bonus'],
      widthPct: w,
      title: `Bonus (challenges + PRs + items): ${Math.round(bonusPts)} pts`,
      label: 'Bonus',
    });
  }

  return segs;
}
